import React, { useState } from 'react';
import {
  Table,
  Header,
  Box,
  Button,
  Link,
  TextFilter,
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
} from '@amzn/awsui-components-core';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { ContentRequest } from '@amzn/awscat-pattern-library-service-model';

import BrowserHistory from '../utils/BrowserHistory';
import constants from '../../common/constants';
import { tablePaginationAriaLabels } from '../../api/UtilsApi';
import RouterPaths from '../../common/RouterPaths';
import { DEFAULT_PAGE_SIZE_OPTIONS, Preferences } from '../common/CommonComponents';

interface CategoriesSuggestion {
  Strategy: ContentRequest[];
  Guide: ContentRequest[];
  Pattern: ContentRequest[];
}

interface TableRow {
  Guide: ContentRequest;
  Pattern: ContentRequest;
  Strategy: ContentRequest;
}

interface ContentRequestCatalogViewProps {
  contentRequests: ContentRequest[];
}

interface ContentRequestLinkprops {
  contentRequest: ContentRequest;
}

interface EmptyStateProps {
  title: string;
  subtitle: string;
}

const columnDefinitions = [
  {
    cell: (item: TableRow) => <ContentRequestLink contentRequest={item.Strategy} />,
    header: 'Strategy',
    id: 'strategy',
  },
  {
    cell: (item: TableRow) => <ContentRequestLink contentRequest={item.Guide} />,
    header: 'Guide',
    id: 'guide',
  },
  {
    cell: (item: TableRow) => <ContentRequestLink contentRequest={item.Pattern} />,
    header: 'Pattern',
    id: 'pattern',
  },
];

const ContentRequestLink = ({ contentRequest }: ContentRequestLinkprops) => {
  if (!contentRequest) {
    return null;
  }
  const { requestId, contentRequestData } = contentRequest;
  const { contentName } = contentRequestData;
  return (
    <Link href={`/review-content-request/${requestId}`} target="_blank">
      {contentName}
    </Link>
  );
};

const categorizeContentRequests = (contentRequests: ContentRequest[]): TableRow[] => {
  const categorizedSuggestions = contentRequests.reduce(
    (acc: CategoriesSuggestion, cur: ContentRequest) => {
      const { contentType } = cur;
      // @ts-expect-error TS7053 - strict migration
      acc[contentType].push(cur);
      return acc;
    },
    { Strategy: [], Guide: [], Pattern: [] },
  );
  const maxLength = Math.max(
    categorizedSuggestions.Strategy.length,
    categorizedSuggestions.Guide.length,
    categorizedSuggestions.Pattern.length,
  );

  const tableViewSuggestion: TableRow[] = [];
  for (let i = 0; i < maxLength; i++) {
    const row = {
      Strategy: categorizedSuggestions.Strategy[i] || null,
      Guide: categorizedSuggestions.Guide[i] || null,
      Pattern: categorizedSuggestions.Pattern[i] || null,
    };
    // @ts-expect-error TS2345 - strict migration
    tableViewSuggestion.push(row);
  }
  return tableViewSuggestion;
};

const filterContentRequest = (item: TableRow, filteringText: string): boolean => {
  const rowItems = [item.Strategy, item.Guide, item.Pattern];
  const filteringTextLowerCase = filteringText.toLocaleLowerCase().trim();
  return rowItems.some(
    (rowItem) =>
      rowItem?.contentRequestData?.contentName.includes(filteringTextLowerCase) ||
      rowItem?.contentRequestData?.description.includes(filteringTextLowerCase),
  );
};

const EmptyState = ({ title, subtitle }: EmptyStateProps) => (
  <Box textAlign="center" color="inherit">
    <b>{title}</b>
    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
      {subtitle}
    </Box>
  </Box>
);

const ContentRequestCatalogView = ({ contentRequests }: ContentRequestCatalogViewProps) => {
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: constants.TABLE_DEFAULT_PAGE_SIZE,
  });
  const categorizedContentRequests = categorizeContentRequests(contentRequests);

  const { items, collectionProps, filterProps, paginationProps } = useCollection(
    categorizedContentRequests,
    {
      filtering: {
        empty: (
          <EmptyState
            title={constants.CONTENT_REQUEST_NO_SUGGESTION}
            subtitle={constants.CONTENT_REQUEST_NO_SUGGESTION_TO_DISPLAY}
          />
        ),
        noMatch: (
          <EmptyState
            title={constants.CONTENT_REQUEST_NO_SUGGESTION}
            subtitle={constants.CONTENT_REQUEST_NO_MATCH}
          />
        ),
        filteringFunction: filterContentRequest,
      },
      pagination: { pageSize: preferences.pageSize },
    },
  );

  const handleAddSuggestion = () => {
    BrowserHistory.push(RouterPaths.CONTENT_REQUEST_CREATION);
  };

  const tableHeader = (
    <Header
      variant="h3"
      actions={
        <Button variant="primary" onClick={handleAddSuggestion}>
          {constants.CONTENT_REQUEST_ADD_SUGGESTION}
        </Button>
      }
      counter={`(${contentRequests.length})`}
    >
      Suggestions
    </Header>
  );

  return (
    <Table
      {...collectionProps}
      columnDefinitions={columnDefinitions}
      resizableColumns
      wrapLines={preferences.wrapLines}
      pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
      items={items}
      header={tableHeader}
      filter={
        <TextFilter
          {...filterProps}
          countText={''}
          filteringAriaLabel={constants.CONTENT_REQUEST_FILTER_LABEL}
        />
      }
      preferences={
        <Preferences disabled={false} preferences={preferences} setPreferences={setPreferences} />
      }
    />
  );
};

export default ContentRequestCatalogView;
